import { Fragment } from 'react';
import { LOCAL_ID_PREFIX } from '../../constants';
import {
    AdvicesEntity,
    DentalExamination,
    DentalProcedure,
    DiagnosisEntityOrSymptomsEntity,
    ExaminationEntity,
    Followup,
    Frequency,
    InjectionsEntity,
    LabTestsEntityOrProceduresEntity,
    LabVitalEntity,
    MedDose,
    MedDoseInfo,
    MedicationsEntity,
    Notes,
    Procedure,
    SelectionEntity,
    VitalsEntity,
} from '../../store/types/prescription';
import {
    DiagnosisIcon,
    InvestigationsIcon,
    MedicinesIcon,
    SymptomsIcon,
} from './rightPanel/workspace/padV2/RowIcons';
import DrugInteractionExceptionPanel from '../common/DrugInteractionExceptionPanel';
import { CreateReferalData } from '../../api/createReferal';
import {
    AdvicesIcon,
    DentalExaminationIcon,
    DentalProcedureIcon,
    LabVital,
    ProcedureIcon,
    ReferIcon,
    canvasIcon,
    followUpIcon,
} from '../common/Icons';
import moment from 'moment';
import { MHC } from '../../store/types/medicalHistory';
import { getPrintConfigs, isMHFilled } from '../../store/selectors/prescriptionPad';
import { MedicalHistorySummary } from './rightPanel/workspace/padV2/MedicalHistoryCardPadV2';
import { PadDForm } from '../../api/pushVisit';
import { useSelector } from 'react-redux';
import { getGenieSetting, getIsICDSuggestionsEnabled } from '../../store/selectors/session';
import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import { TEETH_TO_NAME } from './leftPanel/DentalChart/constants';
import SyringeSolidIcon from '../../icons/solid/syringe-solid';
import { INJECTION_COLUMN } from '../../utils/enums';
import { GeniePadElementsSettingItem } from '../../api/pushGenie';
import { GlobalStateV1 } from '../../store/reducers';
import { CareCanvas } from '../../store/types/CareCanvas';
import { parseHTMLToStringForPipeSeperated } from '../common/htmlParser';

export interface PropertySelection {
    id: string;
    value: string;
}

export const SummaryEmptyView: React.FC = () => {
    return (
        <div className="flex-1 flex flex-col items-center justify-center text-center space-y-32 h-full">
            <img src="/images/empty-summary.png" className="h-256 w-256" />
            <p className="heading-1 bold text-darwin-neutral-500 w-70p">
                You have not yet added any details to your prescription. Once you do, you will start
                seeing them here in the summary.
            </p>
        </div>
    );
};

function renderMedDose(m: MedDoseInfo) {
    return (
        <Fragment>
            <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                {m.dose?.id && m.dose.unit && m.dose.value
                    ? `${m.dose.value} ${m.dose.unit}`
                    : m.dose?.custom}
                {m?.route?.display_name && (
                    <Fragment>
                        <br /> {`Route: ${m.route.display_name}`}
                    </Fragment>
                )}
                {m?.area?.name && (
                    <Fragment>
                        <br /> {`Apply on: ${m.area.name}`}
                    </Fragment>
                )}
            </td>
            <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                {m.frequency?.custom}{' '}
                {m.timing ? (
                    <Fragment>
                        <br /> {`(${m.timing})`}{' '}
                    </Fragment>
                ) : (
                    ''
                )}{' '}
            </td>
            <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                {m.duration?.custom}
                {m.start_from ? (
                    <p> {`(${m.start_from.text}${m.to_day ? ` - ${m.to_day.text}` : ''})`} </p>
                ) : (
                    ''
                )}
            </td>
            <td className="border border-neutral-8 text-neutral-1 py-16 px-8">{m.instruction}</td>
        </Fragment>
    );
}

const renderInjectionRow = ({
    injection,
    routeSpan = 0,
    spanAll = false,
    selectedColumns,
}: {
    routeSpan?: number;
    injection: InjectionsEntity;
    spanAll?: boolean;
    selectedColumns?: INJECTION_COLUMN[];
}) => {
    return (
        <Fragment>
            {selectedColumns?.map((columnType) => {
                switch (columnType) {
                    case INJECTION_COLUMN.DOSE:
                        return (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                            >
                                {injection.dose?.custom}
                            </td>
                        );

                    case INJECTION_COLUMN.ROUTE:
                        return routeSpan ? (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                rowSpan={routeSpan}
                            >
                                {injection.route}
                            </td>
                        ) : null;

                    case INJECTION_COLUMN.INFUSION_RATE:
                        return spanAll ? (
                            routeSpan ? (
                                <td
                                    key={columnType}
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={routeSpan}
                                >
                                    <div className="flex flex-col gap-y-4">
                                        <div>{injection.infusion_rate?.iv_drop_rate?.custom}</div>
                                        <div>{injection.infusion_rate?.iv_rate?.custom}</div>
                                        <div>{injection.infusion_rate?.infusion_time?.custom}</div>
                                    </div>
                                </td>
                            ) : null
                        ) : (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                            >
                                <div className="flex flex-col gap-y-4">
                                    <div>{injection.infusion_rate?.iv_drop_rate?.custom}</div>
                                    <div>{injection.infusion_rate?.iv_rate?.custom}</div>
                                    <div>{injection.infusion_rate?.infusion_time?.custom}</div>
                                </div>
                            </td>
                        );
                    case INJECTION_COLUMN.TIMING:
                        return spanAll ? (
                            routeSpan ? (
                                <td
                                    key={columnType}
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={routeSpan}
                                >
                                    <div className="flex flex-col">{injection.timing}</div>
                                </td>
                            ) : null
                        ) : (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                            >
                                <div className="flex flex-col">{injection.timing}</div>
                            </td>
                        );

                    case INJECTION_COLUMN.DURATION:
                        return spanAll ? (
                            routeSpan ? (
                                <td
                                    key={columnType}
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={routeSpan}
                                >
                                    {injection.duration?.custom}
                                </td>
                            ) : null
                        ) : (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                            >
                                {injection.duration?.custom}
                            </td>
                        );

                    case INJECTION_COLUMN.FREQUENCY:
                        return spanAll ? (
                            routeSpan ? (
                                <td
                                    key={columnType}
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={routeSpan}
                                >
                                    <div className="flex flex-col gap-y-4">
                                        <div>{injection.frequency?.custom}</div>
                                        <div>
                                            {injection.frequency?.time_split?.map((time) => (
                                                <div
                                                    key={String(time?.timing)}
                                                    className="flex flex-col"
                                                >
                                                    <span>
                                                        {moment(time?.timing).format('hh:mm A')}
                                                    </span>
                                                    -<span>{time?.custom}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </td>
                            ) : null
                        ) : (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                            >
                                <div className="flex flex-col gap-y-4">
                                    <div>{injection.frequency?.custom}</div>
                                    <div>
                                        {injection.frequency?.time_split?.map((time) => (
                                            <div
                                                key={String(time?.timing)}
                                                className="flex flex-col"
                                            >
                                                <span>
                                                    {moment(time?.timing).format('hh:mm A')}
                                                </span>
                                                -<span>{time?.custom}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </td>
                        );

                    case INJECTION_COLUMN.INSTRUCTIONS:
                        return spanAll ? (
                            routeSpan ? (
                                <td
                                    key={columnType}
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={routeSpan}
                                >
                                    {injection.instructions}
                                </td>
                            ) : null
                        ) : (
                            <td
                                key={columnType}
                                className="border border-neutral-8 text-neutral-1 py-16 px-8"
                            >
                                {injection.instructions}
                            </td>
                        );

                    default:
                        return null;
                }
            })}
        </Fragment>
    );
};

const LabVitalIcon = LabVital();
export const getNotesAsStringFromHTML = (notes: SelectionEntity[] | undefined) => {
    return notes?.reduce((curString, sel, i) => {
        if (i === 0) {
            return (
                curString + parseHTMLToStringForPipeSeperated(sel.value) + ' ' + (sel.unit || '')
            );
        }
        return curString + ', ' + parseHTMLToStringForPipeSeperated(sel.value) + (sel.unit || '');
    }, '');
};
export const SymptomsComponenent: React.FC<{
    symptoms?: DiagnosisEntityOrSymptomsEntity[];
    sectionName?: string;
}> = (props) => {
    const { symptoms, sectionName } = props;

    return symptoms && symptoms.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <SymptomsIcon />
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Symptoms'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                {symptoms.map((element) => {
                    if (
                        element?.duration?.value ||
                        element?.duration?.custom ||
                        element?.severity?.display ||
                        element?.remark
                    ) {
                        const properties = [
                            `${
                                element?.duration?.value
                                    ? `Since: ${element?.duration?.value} `
                                    : ''
                            }${element?.duration?.unit ? `${element?.duration?.unit} ` : ''}` ||
                            element?.duration?.custom
                                ? `Since: ${element?.duration?.custom}`
                                : '',
                            element?.severity?.display
                                ? `Severity: ${element?.severity?.display}`
                                : '',
                            element?.status?.display ? `Status: ${element?.status?.display}` : '',
                            element?.remark ? `Remark: ${element?.remark}` : '',
                        ].filter(Boolean);

                        return (
                            <div>
                                <span className="font-600 text-neutral-black">
                                    {element.name}
                                    {properties.length ? ':' : ''}{' '}
                                </span>
                                {properties.join(' | ')}
                                <br />
                            </div>
                        );
                    }

                    const properties = element.properties;
                    return (
                        <div>
                            <span className="font-600 text-neutral-black">
                                {element.name}
                                {properties ? ':' : ''}{' '}
                            </span>
                            {properties &&
                                Object?.entries(properties)?.reduce(
                                    (previousValue, [propertyId, property]) => {
                                        const isValuesEmpty = !property?.selection?.filter(
                                            (selection) => selection.value,
                                        ).length;

                                        if (isValuesEmpty) {
                                            return previousValue;
                                        }
                                        if (propertyId === 'pg-5243973452') {
                                            return (
                                                previousValue +
                                                (previousValue.length ? '  |  ' : '') +
                                                property.name +
                                                ': ' +
                                                getNotesAsStringFromHTML(property?.selection)
                                            );
                                        }

                                        return (
                                            previousValue +
                                            (previousValue.length ? '  |  ' : '') +
                                            property.name +
                                            ': ' +
                                            property?.selection?.reduce((curString, sel, i) => {
                                                if (i === 0) {
                                                    return (
                                                        curString +
                                                        sel.value +
                                                        ' ' +
                                                        (sel.unit || '')
                                                    );
                                                }

                                                return (
                                                    curString + ', ' + sel.value + (sel.unit || '')
                                                );
                                            }, '')
                                        );
                                    },
                                    '' as string,
                                )}
                            <br />
                        </div>
                    );
                })}
            </div>
        </div>
    ) : null;
};

export const DiagnosisComponent: React.FC<{
    diagnosis?: DiagnosisEntityOrSymptomsEntity[];
    sectionName?: string;
}> = (props) => {
    const { diagnosis: diagonis, sectionName } = props;
    const isICDSuggestionsEnable = useSelector(getIsICDSuggestionsEnabled);

    return diagonis && diagonis.length > 0 ? (
        <div className="space-y-8 bg-white pl-16 pt-8 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <DiagnosisIcon />
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Diagnosis'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                {diagonis.map((element) => {
                    if (
                        element?.duration?.value ||
                        element?.duration?.custom ||
                        element?.severity?.display ||
                        element?.remark
                    ) {
                        const properties = [
                            `${
                                element?.duration?.value
                                    ? `Since: ${element?.duration?.value} `
                                    : ''
                            }${element?.duration?.unit ? `${element?.duration?.unit} ` : ''}` ||
                            element?.duration?.custom
                                ? `Since: ${element?.duration?.custom}`
                                : '',
                            element?.severity?.display
                                ? `Severity: ${element?.severity?.display}`
                                : '',
                            element?.status?.display ? `Status: ${element?.status?.display}` : '',
                            element?.remark ? `Remark: ${element?.remark}` : '',
                        ].filter(Boolean);

                        return (
                            <div>
                                <span className="font-600 text-neutral-black">
                                    {isICDSuggestionsEnable
                                        ? `${element?.name}${
                                              element?.icd10_code ? ` - ${element?.icd10_code}` : ''
                                          }`
                                        : element?.name}
                                    {properties.length ? ':' : ''}{' '}
                                </span>
                                {properties.join(' | ')}
                                <br />
                            </div>
                        );
                    }

                    const properties = element.properties;

                    return (
                        <div>
                            <span className="font-600 text-neutral-black">
                                {isICDSuggestionsEnable
                                    ? `${element?.name}${
                                          element?.icd10_code ? ` - ${element?.icd10_code}` : ''
                                      }`
                                    : element?.name}
                                {properties ? ':' : ''}{' '}
                            </span>
                            {properties &&
                                Object?.entries(properties)?.reduce(
                                    (previousValue, [propertyId, property]) => {
                                        const isValuesEmpty = !property?.selection?.filter(
                                            (selection) => selection.value,
                                        ).length;

                                        if (isValuesEmpty) {
                                            return previousValue;
                                        }

                                        if (propertyId === 'pg-5243973452') {
                                            return (
                                                previousValue +
                                                (previousValue.length ? '  |  ' : '') +
                                                property.name +
                                                ': ' +
                                                getNotesAsStringFromHTML(property?.selection)
                                            );
                                        }

                                        return (
                                            previousValue +
                                            (previousValue.length ? '  |  ' : '') +
                                            property.name +
                                            ': ' +
                                            property?.selection?.reduce((curString, sel, i) => {
                                                if (i === 0) {
                                                    return (
                                                        curString +
                                                        sel.value +
                                                        ' ' +
                                                        (sel.unit || '')
                                                    );
                                                }

                                                return (
                                                    curString + ', ' + sel.value + (sel.unit || '')
                                                );
                                            }, '')
                                        );
                                    },
                                    '' as string,
                                )}
                            <br />
                        </div>
                    );
                })}
            </div>
        </div>
    ) : null;
};

export const MedTableComponent: React.FC<{
    medications?: MedicationsEntity[];
    sectionName?: string;
}> = (props) => {
    const { medications: meds, sectionName } = props;
    const printConfigs = useSelector(getPrintConfigs);
    const medPrintConfig = printConfigs?.medications;
    const taperingDoseTitleDisplay =
        typeof medPrintConfig === 'object' && 'taperingDoseTitleDisplay' in medPrintConfig
            ? medPrintConfig.taperingDoseTitleDisplay
            : false;

    return meds && meds.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <MedicinesIcon />
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Medications'}
                    </p>
                </div>
            </div>
            <table className="w-full border-seperate border border-neutral-8">
                <tbody>
                    <tr className="bg-neutral-9">
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            Sl. No
                        </td>
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            Medicine
                        </td>
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            Dose
                        </td>
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            Frequency
                        </td>
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            duration
                        </td>
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            REMARKS
                        </td>
                    </tr>
                    {meds.map((m, index) => (
                        <Fragment>
                            <tr key={m.id}>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                    {index + 1}
                                </td>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                    {m.name} {m.product_type ? `(${m.product_type})` : ''}{' '}
                                    {m.generic_name}
                                </td>
                                {renderMedDose(m)}
                            </tr>
                            {m.tapering_dose?.map((td, ii) => (
                                <tr key={m.id}>
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                        {index + 1}.{ii + 1}
                                    </td>
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                        {taperingDoseTitleDisplay ? (
                                            <>
                                                {m.name}{' '}
                                                {m.product_type ? `(${m.product_type})` : ''}{' '}
                                                {m.generic_name}
                                            </>
                                        ) : (
                                            ''
                                        )}
                                    </td>
                                    {renderMedDose(td)}
                                </tr>
                            ))}
                        </Fragment>
                    ))}
                </tbody>
            </table>

            <div className="mt-8 mb-4">
                <DrugInteractionExceptionPanel />
            </div>
        </div>
    ) : null;
};

export const InjectionTableComponent: React.FC<{
    injections?: InjectionsEntity[];
    sectionName?: string;
}> = (props) => {
    const injectionTableConfig = useSelector((state) =>
        getGenieSetting(state as GlobalStateV1, 'injectionTableConfig'),
    ) as GeniePadElementsSettingItem[];

    const tableElements = injectionTableConfig
        .filter((config) => config.isShown)
        .map((config) => config.id);
    const selectedColumns = [INJECTION_COLUMN.DRUG, ...(tableElements as INJECTION_COLUMN[])];
    const { injections, sectionName } = props;

    return injections && injections.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="bg-darwin-accent-medicines-pink-800 rounded-4 py-7 px-5">
                        <SyringeSolidIcon className="w-16 h-12 text-white" />
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Injections'}
                    </p>
                </div>
            </div>
            <table className="w-full border-seperate border border-neutral-8">
                <tbody>
                    <tr className="bg-neutral-9">
                        <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8">
                            Sl. No
                        </td>
                        {selectedColumns?.map((columnType) => {
                            if (!columnType) return null;

                            const columnConfig = {
                                width:
                                    columnType === INJECTION_COLUMN.DRUG
                                        ? '25%'
                                        : columnType === INJECTION_COLUMN.ROUTE
                                        ? '7%'
                                        : undefined,
                                title: (() => {
                                    switch (columnType) {
                                        case INJECTION_COLUMN.DRUG:
                                            return 'Injection';
                                        case INJECTION_COLUMN.DOSE:
                                            return 'Dose';
                                        case INJECTION_COLUMN.ROUTE:
                                            return 'Route';
                                        case INJECTION_COLUMN.INFUSION_RATE:
                                            return 'Infusion Rate';
                                        case INJECTION_COLUMN.FREQUENCY:
                                            return 'Frequency';
                                        case INJECTION_COLUMN.DURATION:
                                            return 'Duration';
                                        case INJECTION_COLUMN.TIMING:
                                            return 'Timing';
                                        case INJECTION_COLUMN.INSTRUCTIONS:
                                            return 'Instructions';
                                        default:
                                            return null;
                                    }
                                })(),
                            };

                            if (!columnConfig.title) return null;

                            return (
                                <td
                                    key={columnType}
                                    className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8"
                                    width={columnConfig.width}
                                >
                                    {columnConfig.title}
                                </td>
                            );
                        })}
                    </tr>
                    {injections.map((injection, index) => (
                        <Fragment>
                            <tr key={injection.id}>
                                <td
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={
                                        Math.max(
                                            injection.added_drug?.length || 0,
                                            injection.tapering_dose?.length || 0,
                                        ) + 1
                                    }
                                >
                                    {index + 1}.
                                </td>
                                <td
                                    className="border border-neutral-8 text-neutral-1 py-16 px-8"
                                    rowSpan={(injection.tapering_dose?.length || 0) + 1}
                                >
                                    {injection.name} {injection.generic_name}
                                </td>
                                {renderInjectionRow({
                                    injection,
                                    routeSpan:
                                        Math.max(
                                            injection.added_drug?.length || 0,
                                            injection.tapering_dose?.length || 0,
                                        ) + 1,
                                    spanAll: !!injection.added_drug?.length,
                                    selectedColumns,
                                })}
                            </tr>
                            {injection.added_drug?.map((drug) => (
                                <tr key={drug.id}>
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                        {drug.name}
                                        {drug.generic_name}
                                    </td>
                                    {renderInjectionRow({
                                        injection: drug,
                                        spanAll: true,
                                        selectedColumns,
                                    })}
                                </tr>
                            ))}
                            {injection.tapering_dose?.map((td) => (
                                <tr key={injection.id}>
                                    {renderInjectionRow({ injection: td, selectedColumns })}
                                </tr>
                            ))}
                        </Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    ) : null;
};

export const ReferComponent: React.FC<{
    refer?: CreateReferalData | undefined;
    sectionName?: string;
}> = (props) => {
    const { refer, sectionName } = props;

    return refer ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-medical-history-orange-200 rounded-4">
                        {ReferIcon('')}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Referral Details'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                <p className="text-neutral-black">
                    <ul>
                        {refer.doc && refer.doc.length > 0 ? (
                            refer.doc.map((item, index) => {
                                const clinicAdress = [
                                    item?.clinic?.name,
                                    item?.clinic?.landmark,
                                    item?.clinic?.city,
                                    item?.clinic?.state,
                                    item?.clinic?.pincode,
                                ]
                                    .filter(Boolean)
                                    .join(', ');

                                return (
                                    <li key={index + 'refer'}>
                                        &#8226;{' '}
                                        {[
                                            item.type === 'speciality'
                                                ? item.s_name
                                                : ['Dr.', item.name?.f, item.name?.l]
                                                      .filter(Boolean)
                                                      .join(' '),
                                            item.type !== 'speciality' && item?.s_name,
                                            item?.phone?.value
                                                ? `${
                                                      item?.phone?.code
                                                          ? `${item?.phone?.code} `
                                                          : ''
                                                  }${item?.phone?.value}`
                                                : undefined,
                                            clinicAdress,
                                        ]
                                            .filter(Boolean)
                                            .join(' | ')}
                                    </li>
                                );
                            })
                        ) : (
                            <p className="w-full text-neutral-3 text-13">[EKA]</p>
                        )}
                    </ul>
                </p>
            </div>
        </div>
    ) : null;
};

export const FollowUpComponent: React.FC<{
    followup?: Followup | undefined;
    sectionName?: string;
}> = (props) => {
    const { followup: followUp } = props;

    return followUp && (followUp.date || followUp.notes) ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-neutral-800 rounded-4">
                        {followUpIcon('fill-white')}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {props?.sectionName || 'Follow Up'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                <p className="text-neutral-black">
                    {followUp.date ? moment(followUp.date).format('Do MMM YYYY') : ''}{' '}
                </p>
                <p style={{ whiteSpace: 'break-spaces' }}>{followUp.notes}</p>
            </div>
        </div>
    ) : null;
};

export const MedicalHistoryComponent: React.FC<{
    medicalHistory?: MHC | undefined;
    sectionName?: string;
}> = (props) => {
    const { medicalHistory: mh, sectionName } = props;

    return mh && isMHFilled(mh) ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-medical-history-orange-900 rounded-4">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            role="img"
                            viewBox="0 0 11 13"
                            className={`w-11 h-12 fill-white`}
                        >
                            <path d="M10.75 8.375V1.625C10.75 1.01562 10.2344 0.5 9.625 0.5H2.5C1.23438 0.5 0.25 1.50781 0.25 2.75V10.25C0.25 11.5156 1.23438 12.5 2.5 12.5H10C10.3984 12.5 10.75 12.1719 10.75 11.7734C10.75 11.5156 10.5859 11.2812 10.375 11.1406V9.24219C10.5859 9.00781 10.75 8.72656 10.75 8.375ZM3.25 4.39062C3.25 4.20312 3.41406 4.01562 3.625 4.01562H4.86719V2.75C4.86719 2.5625 5.03125 2.375 5.24219 2.375H6.46094C6.64844 2.375 6.83594 2.5625 6.83594 2.75V4.01562H8.125C8.3125 4.01562 8.5 4.20312 8.5 4.39062V5.60938C8.5 5.82031 8.3125 5.98438 8.125 5.98438H6.85938V7.25C6.85938 7.46094 6.67188 7.625 6.48438 7.625H5.26562C5.05469 7.625 4.89062 7.46094 4.89062 7.25V5.98438H3.625C3.41406 5.98438 3.25 5.82031 3.25 5.60938V4.39062ZM9.25 11H2.5C2.07812 11 1.75 10.6719 1.75 10.25C1.75 9.85156 2.07812 9.5 2.5 9.5H9.25V11Z" />
                        </svg>
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Medical History'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">{MedicalHistorySummary(mh)}</div>
        </div>
    ) : null;
};

export const DynamicFormComponent: React.FC<{ section: PadDForm | undefined }> = (props) => {
    const { section } = props;

    return !section ? null : (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-textColor-secondary rounded-4">
                        <svg
                            className="w-16 h-16 fill-white"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 384 512"
                        >
                            <path d="M320 64h-49.61C262.1 27.48 230.7 0 192 0S121 27.48 113.6 64H64C28.65 64 0 92.66 0 128v320c0 35.34 28.65 64 64 64h256c35.35 0 64-28.66 64-64V128C384 92.66 355.3 64 320 64zM192 48c13.23 0 24 10.77 24 24S205.2 96 192 96S168 85.23 168 72S178.8 48 192 48zM336 448c0 8.82-7.178 16-16 16H64c-8.822 0-16-7.18-16-16V128c0-8.82 7.178-16 16-16h18.26C80.93 117.1 80 122.4 80 128v16C80 152.8 87.16 160 96 160h192c8.836 0 16-7.164 16-16V128c0-5.559-.9316-10.86-2.264-16H320c8.822 0 16 7.18 16 16V448zM272 288H224V240C224 231.2 216.8 224 208 224h-32C167.2 224 160 231.2 160 240V288H112C103.2 288 96 295.2 96 304v32C96 344.8 103.2 352 112 352H160v48C160 408.8 167.2 416 176 416h32c8.801 0 16-7.203 16-16V352h48c8.801 0 16-7.203 16-16v-32C288 295.2 280.8 288 272 288z"></path>
                        </svg>
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">{section?.name}</p>
                </div>
            </div>
            <div className="space-y-10">
                <ul>
                    {section?.elements?.map((e) => (
                        <li key={e.id}>
                            {' '}
                            <span className="font-700">{e.name}:</span>{' '}
                            <span className="">{e.value}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export const LabVitalsComponents: React.FC<{
    labVitals?: LabVitalEntity[];
    sectionName?: string;
}> = (props) => {
    const { labVitals, sectionName } = props;

    return labVitals && labVitals.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-bright-yellow-900 rounded-4">
                        {LabVitalIcon}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Lab Results'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                <p className="text-neutral-black">
                    {labVitals.map((l, index) => {
                        const text = [l.value, l.unit?.name, l.interpretation?.value || '-']
                            .filter(Boolean)
                            .join(' ');
                        return (
                            <Fragment>
                                <span key={l.id}>
                                    <span className="font-600">{l.name}</span>
                                    <span className="font-500">{text ? `: ${text}` : ''}</span>{' '}
                                    <span>{l.remark || ''}</span>
                                </span>
                                {index < labVitals.length - 1 && <span className="bold"> | </span>}
                            </Fragment>
                        );
                    })}
                </p>
            </div>
        </div>
    ) : null;
};

export const LabTestsComponents: React.FC<{
    labTests?: LabTestsEntityOrProceduresEntity[];
    sectionName?: string;
}> = (props) => {
    const { labTests: labtest, sectionName } = props;

    return labtest && labtest.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <InvestigationsIcon />
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Investigations'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                <p className="text-neutral-black break-all space-y-10">
                    {labtest.map((l) => (
                        <div>
                            {l.name}{' '}
                            {l.remark ? (
                                <>
                                    <span className="text-16 font-700 text-darwin-neutral-700">
                                        |
                                    </span>{' '}
                                    {l.remark}
                                </>
                            ) : (
                                ''
                            )}
                        </div>
                    ))}
                </p>
            </div>
        </div>
    ) : null;
};

export const VitalsComponent: React.FC<{ vitals?: VitalsEntity[] }> = (props) => {
    const { vitals } = props;
    return vitals && vitals.length > 0 ? (
        <div className="space-y-4 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-dark-blue-800 rounded-4">
                        <svg
                            className="w-16 h-12 fill-white"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M476.7 20.35c-37.5-30.63-95.49-26.26-131.9 10.25l-239.6 241.2C99.36 277.8 96.02 285.9 96.02 294.4l.0006 87.71l-88.99 89.02c-9.375 9.377-9.375 24.63 0 33.88c9.373 9.377 24.62 9.377 33.87 0l88.99-89.02l86.64-.0002c8.467 0 16.59-3.355 22.59-9.332l244.9-243.1C518.9 127.8 526.2 61.11 476.7 20.35zM450.1 128.6l-240.1 239.4H144V300.9l35-35.27l33.67 33.67C215.8 302.4 219.9 304 224 304s8.188-1.562 11.31-4.688c6.25-6.25 6.25-16.38 0-22.62L201.6 242.9l41.21-41.54l33.92 33.92C279.8 238.4 283.9 240 288 240s8.188-1.562 11.31-4.688c6.25-6.25 6.25-16.38 0-22.62l-34.01-34.01l41.21-41.54l34.17 34.17C343.8 174.4 347.9 176 352 176s8.188-1.562 11.31-4.688c6.25-6.25 6.25-16.38 0-22.62l-34.25-34.25l49.69-50.07c19.12-19.13 48.75-22.13 67.37-7C471.6 78.36 467.2 111.5 450.1 128.6z"></path>
                        </svg>
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">Vitals</p>
                </div>
            </div>
            <div className="flex items-center flex-wrap -ml-56">
                {vitals.map((v) => (
                    <div key={v.id} className="space-y-4 mt-4 ml-56">
                        <p className="text-14 text-neutral-5">{v.dis_name}</p>
                        <p className="text-20 text-neutral-black">
                            {v.value.qt}
                            <span className="text-12 text-neutral-5">{v.value.unit}</span>
                        </p>
                    </div>
                ))}
            </div>
        </div>
    ) : null;
};

export const ExaminationsComponent: React.FC<{
    examinations?: ExaminationEntity[];
    sectionName?: string;
}> = (props) => {
    const { examinations, sectionName } = props;

    return examinations && examinations.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-examination-findings-sky-blue-900 rounded-4">
                        <svg
                            className="w-16 h-12 fill-white"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 13 13"
                        >
                            <path
                                d="M11.0379 3.125C9.9832 3.125 9.16289 3.96875 9.16289 5C9.16289 5.77344 9.6082 6.42969 10.2879 6.73438V8.58594C10.2879 9.92188 9.09258 11.0234 7.66289 11.0234C6.25664 11.0234 5.08476 9.96875 5.03789 8.67969C6.72539 8.32812 8.03789 6.82812 8.03789 5V1.36719C8.03789 1.10938 7.82695 0.875 7.56914 0.828125L6.09258 0.523438C5.78789 0.453125 5.50664 0.664062 5.43633 0.96875L5.36601 1.32031C5.2957 1.625 5.50664 1.92969 5.81133 2L6.51445 2.14062V4.97656C6.51445 6.21875 5.53008 7.25 4.28789 7.25C3.0457 7.27344 2.01445 6.26562 2.01445 5V2.14062L2.74101 2C3.0457 1.92969 3.25664 1.64844 3.18633 1.34375L3.11601 0.96875C3.0457 0.664062 2.76445 0.453125 2.45976 0.523438L0.983201 0.804688C0.725389 0.875 0.537889 1.10938 0.537889 1.36719V5C0.537889 6.82812 1.82695 8.32812 3.53789 8.67969C3.58476 10.8125 5.41289 12.5 7.66289 12.5C9.93633 12.5 11.7879 10.7422 11.7879 8.5625V6.73438C12.4441 6.42969 12.9129 5.77344 12.9129 5.02344C12.9129 3.96875 12.0691 3.125 11.0379 3.125ZM11.0379 5.5625C10.7098 5.5625 10.4754 5.32812 10.4754 5C10.4754 4.69531 10.7098 4.4375 11.0379 4.4375C11.3426 4.4375 11.6004 4.69531 11.6004 5C11.6004 5.32812 11.3426 5.5625 11.0379 5.5625Z"
                                fill="white"
                            />
                        </svg>
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Examination Findings'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                {examinations.map((e) => (
                    <p key={e.id} className="text-neutral-black">
                        {e.name} {e.notes ? `- ${e.notes}` : ''}
                    </p>
                ))}
            </div>
        </div>
    ) : null;
};

export const PPNotesComponent: React.FC<{
    prescriptionNotes?: Notes | undefined;
    sectionName?: string;
}> = (props) => {
    const { prescriptionNotes: notes, sectionName } = props;

    return notes ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-textColor-secondary rounded-4">
                        <svg
                            className="w-16 h-12 fill-white"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 384 512"
                        >
                            <path d="M320 64h-49.61C262.1 27.48 230.7 0 192 0S121 27.48 113.6 64H64C28.65 64 0 92.66 0 128v320c0 35.34 28.65 64 64 64h256c35.35 0 64-28.66 64-64V128C384 92.66 355.3 64 320 64zM192 48c13.23 0 24 10.77 24 24S205.2 96 192 96S168 85.23 168 72S178.8 48 192 48zM336 448c0 8.82-7.178 16-16 16H64c-8.822 0-16-7.18-16-16V128c0-8.82 7.178-16 16-16h18.26C80.93 117.1 80 122.4 80 128v16C80 152.8 87.16 160 96 160h192c8.836 0 16-7.164 16-16V128c0-5.559-.9316-10.86-2.264-16H320c8.822 0 16 7.18 16 16V448zM272 288H224V240C224 231.2 216.8 224 208 224h-32C167.2 224 160 231.2 160 240V288H112C103.2 288 96 295.2 96 304v32C96 344.8 103.2 352 112 352H160v48C160 408.8 167.2 416 176 416h32c8.801 0 16-7.203 16-16V352h48c8.801 0 16-7.203 16-16v-32C288 295.2 280.8 288 272 288z"></path>
                        </svg>
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {sectionName || 'Prescription Notes'}
                    </p>
                </div>
            </div>
            <div style={{ whiteSpace: 'break-spaces' }} className="space-y-10">
                <div
                    className="flex flex-col tiny-mce"
                    dangerouslySetInnerHTML={{ __html: notes?.text }}
                />
            </div>
        </div>
    ) : null;
};

export const AdvicesComponent: React.FC<{ advices?: AdvicesEntity[]; sectionName?: string }> = (
    props,
) => {
    const { advices } = props;

    return advices && advices.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-mauve-800 rounded-4">
                        {AdvicesIcon('fill-white')}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        {props?.sectionName || 'Advices'}
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                {advices.map((a) => (
                    <p key={a.id} className="text-neutral-black">
                        <div
                            className="flex flex-col tiny-mce"
                            dangerouslySetInnerHTML={{ __html: a?.text }}
                        />
                    </p>
                ))}
            </div>
        </div>
    ) : null;
};

export const DentalExaminationsComponent: React.FC<{ dentalExaminations?: DentalExamination[] }> = (
    props,
) => {
    const { dentalExaminations } = props;
    const dentalExaminationIcon = DentalExaminationIcon('w-16 h-12 fill-white');
    const groupByTooth = groupBy(dentalExaminations, (i) => i.group_id || i.teeth_id);

    return dentalExaminations && dentalExaminations.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-green-primary rounded-4">
                        {dentalExaminationIcon}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        Dental Examinations / Past Procedures
                    </p>
                </div>
            </div>
            <div className="space-y-10">
                {Object.entries(groupByTooth).map((t) => {
                    let teeth = uniq(
                        t[1].map((i) => {
                            return `${TEETH_TO_NAME[i.teeth_id || ''] || `T${i.teeth_id}`} ${
                                i.surfaces && i.surfaces.length > 0
                                    ? `(${i.surfaces?.map((i) => i.name).join(', ')})`
                                    : ''
                            }`;
                        }),
                    ).join(', ');
                    if (t[0].length === 2) {
                        let surfaces: string[] = [];
                        t[1].forEach((i) => i.surfaces?.forEach((i) => surfaces.push(i.name)));
                        surfaces = uniq(surfaces);
                        teeth = `${
                            TEETH_TO_NAME[t[1][0].teeth_id || ''] || `T${t[1][0].teeth_id}`
                        } ${surfaces && surfaces.length > 0 ? `(${surfaces?.join(', ')})` : ''}`;
                    }
                    const examinations = uniq(
                        t[1].map((i) => {
                            const sinceAndRemark = [
                                i.since ? `Since: ${moment(i.since).format('Do MMM YY')}` : '',
                                i.remark?.trim() ? `Notes: ${i.remark.trim()}` : '',
                            ]
                                .filter(Boolean)
                                .join(', ');
                            return `${i.name} ${sinceAndRemark && `(${sinceAndRemark})`}`;
                        }),
                    ).join(', ');
                    return (
                        <p className="text-neutral-black">
                            <span className="font-700">
                                {TEETH_TO_NAME[teeth] || teeth || 'T'}:{' '}
                            </span>
                            <span>{examinations}</span>
                        </p>
                    );
                })}
            </div>
        </div>
    ) : null;
};

export const DentalProceduresComponent: React.FC<{ dentalProcedures?: DentalProcedure[] }> = (
    props,
) => {
    const { dentalProcedures } = props;
    const dentalProcedureIcon = DentalProcedureIcon('w-16 h-12 fill-white');
    const showColumns: { [key: string]: boolean } = {};
    dentalProcedures?.forEach(
        ({ teeth_ids, surfaces, visits, start_on, remark, conducted_by, assisted_by }) => {
            if (teeth_ids && teeth_ids?.length > 0) {
                showColumns['teeth_ids'] = true;
            }
            if (surfaces && surfaces?.length > 0) {
                showColumns['surfaces'] = true;
            }
            if (visits) {
                showColumns['visits'] = true;
            }
            if (start_on) {
                showColumns['start_on'] = true;
            }
            if (remark || conducted_by || assisted_by) {
                showColumns['remark'] = true;
            }
        },
    );

    return dentalProcedures && dentalProcedures.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-medicines-pink-800 rounded-4">
                        {dentalProcedureIcon}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">
                        Dental Procedures
                    </p>
                </div>
            </div>
            <table className="w-full border-seperate border border-neutral-8">
                <tbody>
                    <tr className="bg-neutral-9">
                        <td
                            className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                            style={{ width: '4.5%' }}
                        >
                            *
                        </td>
                        <td
                            className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8"
                            style={{ width: '25%' }}
                        >
                            Procedure
                        </td>
                        {showColumns['teeth_ids'] && (
                            <td className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center">
                                Teeth
                            </td>
                        )}
                        {showColumns['surfaces'] && (
                            <td
                                className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                                style={{ width: '16%' }}
                            >
                                Surfaces
                            </td>
                        )}
                        {showColumns['visits'] && (
                            <td
                                className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                                style={{ width: '7.5%' }}
                            >
                                Visits
                            </td>
                        )}
                        {showColumns['start_on'] && (
                            <td
                                className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                                style={{ maxWidth: '17.5%' }}
                            >
                                Date
                            </td>
                        )}
                        {showColumns['remark'] && (
                            <td
                                className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                                style={{ minWidth: '15%' }}
                            >
                                Notes
                            </td>
                        )}
                    </tr>
                    {dentalProcedures.map((i, index) => {
                        const {
                            name,
                            teeth_ids,
                            surfaces,
                            start_on,
                            remark,
                            visits,
                            conducted_by,
                            assisted_by,
                        } = i;
                        return (
                            <tr>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center">
                                    {index + 1}
                                </td>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                    {name || '-'}
                                </td>
                                {showColumns['teeth_ids'] && (
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center">
                                        {teeth_ids?.join(', ') || '-'}
                                    </td>
                                )}
                                {showColumns['surfaces'] && (
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center">
                                        {surfaces?.map((i) => i.name)?.join(', ') || '-'}
                                    </td>
                                )}
                                {showColumns['visits'] && (
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center">
                                        {visits || '-'}
                                    </td>
                                )}
                                {showColumns['start_on'] && (
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center">
                                        {start_on ? moment(start_on).format('Do MMM YY') : '-'}
                                    </td>
                                )}
                                {showColumns['remark'] && (
                                    <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center space-y-3">
                                        <p>
                                            {remark
                                                ? remark
                                                : conducted_by || assisted_by
                                                ? ''
                                                : '-'}
                                        </p>
                                        {conducted_by && (
                                            <p>
                                                <span className="font-700">Conducted By:</span>{' '}
                                                {conducted_by}
                                            </p>
                                        )}
                                        {assisted_by && (
                                            <p>
                                                <span className="font-700">Assisted By:</span>{' '}
                                                {assisted_by}
                                            </p>
                                        )}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    ) : null;
};

export const OpSummaryComponent: React.FC<{
    elements?: { [k: string]: { value: string; custom?: string | undefined } }[];
    heading?: string;
    icon?: JSX.Element;
}> = (props) => {
    const { elements, heading, icon } = props;

    return elements && elements.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-accent-dark-green rounded-4">
                        {icon}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">{heading}</p>
                </div>
            </div>
            <ul>
                {elements.map((i, index) => (
                    <li>
                        <b>{i.eye.value}</b>-{' '}
                        <span>
                            {Object.entries(i)
                                .filter(
                                    ([k, v]) =>
                                        k !== 'id' && k !== 'eye' && k !== 'name' && v && v.custom,
                                )
                                .map(([k, v]) => (
                                    <span>
                                        <b>{k.toUpperCase()}:</b> {v.custom}{' '}
                                    </span>
                                ))}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    ) : null;
};

export const ProceduresComponent: React.FC<{ procedures?: Procedure[] }> = (props) => {
    const { procedures } = props;
    const procedureIcon = ProcedureIcon('w-16 h-12 fill-white');

    return procedures && procedures.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-neutral-2 rounded-4">
                        {procedureIcon}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">Procedures</p>
                </div>
            </div>
            <table className="w-full border-seperate border border-neutral-8">
                <tbody>
                    <tr className="bg-neutral-9">
                        <td
                            className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                            style={{ width: '5%' }}
                        >
                            *
                        </td>
                        <td
                            className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-left"
                            style={{ width: '30%' }}
                        >
                            Procedure
                        </td>
                        <td
                            className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                            style={{ width: '17.5%' }}
                        >
                            Date
                        </td>
                        <td
                            className="border border-neutral-8 uppercase text-12 font-700 text-neutral-5 pt-20 pb-16 pl-8 text-center"
                            style={{ width: '47.5%' }}
                        >
                            Notes
                        </td>
                    </tr>
                    {procedures.map((i, index) => {
                        const { name, notes, date } = i;
                        return (
                            <tr>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center">
                                    {index + 1}
                                </td>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8">
                                    {name || '-'}
                                </td>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center space-y-3">
                                    {date ? moment(date).format('Do MMM YY') : '-'}
                                </td>
                                <td className="border border-neutral-8 text-neutral-1 py-16 px-8 text-center space-y-3">
                                    {notes || '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    ) : null;
};

export const CareCanvasComponent: React.FC<{ careCanvas?: CareCanvas[] }> = (props) => {
    const { careCanvas } = props;
    const careCanvasIcon = canvasIcon('w-14 h-13 fill-white relative -top-1');

    return careCanvas && careCanvas.length > 0 ? (
        <div className="space-y-8 bg-white pt-8 pl-16 pr-8 pb-24">
            <div className="flex items-center justify-between ">
                <div className="flex items-center space-x-10">
                    <div className="flex items-center justify-center w-26 h-26 bg-darwin-accent-mauve-800 rounded-4">
                        {careCanvasIcon}
                    </div>
                    <p className="text-12 text-neutral-black font-700 uppercase">Canvas</p>
                </div>
            </div>
            {careCanvas.map(({ final_image }) => (
                <div>
                    <img
                        src={final_image}
                        style={{ maxWidth: '100%', maxHeight: '100%', margin: '6px auto' }}
                    />
                </div>
            ))}
        </div>
    ) : null;
};
